<template>
  <div class="gradient-color-slider">
    <!-- Gradient Preview Bar -->
    <div class="gradient-bar-container">
      <div
        ref="gradientBar"
        class="gradient-bar"
        :style="{ background: gradientCss }"
        @click="handleBarClick"
      >
        <!-- Color Stop Handles -->
        <div
          v-for="color in colors"
          :key="color.id"
          class="color-handle"
          :style="{
            left: `${getStopPercentage(color.stop)}%`,
            transform: `translateX(-50%)`,
          }"
          @mousedown="startDrag(color.id, $event)"
          @touchstart="startDrag(color.id, $event)"
        >
          <div class="stop-display" :style="getStopDisplayStyle(color.stop)">
            <UITextXsMedium class="font-semibold"
              >{{ color.stop.replace("%", "") }}%</UITextXsMedium
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Color Stop List -->
    <div class="flex flex-col gap-4">
      <UITextSmMedium>{{ t("common.stops") }}</UITextSmMedium>
      <div class="color-stops-list">
        <div v-for="color in colors" :key="color.id" class="color-stop-item">
          <!-- Color Label -->
          <UITextXsMedium class="color-label">{{ getColorLabel(color) }}</UITextXsMedium>

          <!-- Right-aligned controls -->
          <div class="right-controls">
            <!-- Color Picker -->
            <div class="color-picker-container">
              <UIColorPicker
                :value="color.color"
                type="default"
                @update:value="(value) => updateColor(color.id, 'color', value)"
              />
            </div>

            <!-- Stop Input -->
            <div class="stop-input-container">
              <UIInputNumber
                :id="`stop-input-${color.id}`"
                :model-value="parseInt(color.stop.replace('%', ''))"
                size="medium"
                :show-button="false"
                :precision="0"
                :min="0"
                :max="100"
                placeholder=""
                class="stop-input w-[78px]"
                @update:model-value="(value) => updateColor(color.id, 'stop', value + '%')"
              />
            </div>

            <!-- Delete Button -->
            <UIButton
              :id="`delete-color-${color.id}`"
              :disabled="isDefaultColor(color)"
              :quaternary="true"
              size="small"
              class="p-0 text-gray-500 disabled:text-gray-300"
              @click="removeColor(color.id)"
            >
              <XIcon class="h-5 w-5" />
            </UIButton>
          </div>
        </div>
      </div>

      <!-- Add Color Button -->
      <UIButton
        id="add-color"
        :disabled="colors.length >= 10"
        :text="true"
        class="self-start"
        @click="addColor"
      >
        <PlusIcon class="mr-2 h-4 w-4" />
        <UITextSmMedium class="font-semibold">{{ t("common.addColor") }}</UITextSmMedium>
      </UIButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PlusIcon, XIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UIColorPicker,
  UIInputNumber,
  UITextSmMedium,
  UITextXsMedium,
} from "@gohighlevel/ghl-ui";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ColorStop } from "./GradientUtils";

interface Props {
  colors: ColorStop[];
  gradientType: string;
  gradientAngle: number;
}

interface Emits {
  (e: "update:colors", colors: ColorStop[]): void;
  (e: "add-color", percentage?: number): void;
  (e: "remove-color", id: number): void;
  (e: "update-color", id: number, field: "color" | "stop", value: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const { t } = useI18n();

const gradientBar = ref<HTMLElement>();
const isDragging = ref(false);
const draggedId = ref<number | null>(null);
const dragStartX = ref(0);
const barStartX = ref(0);

const gradientCss = computed(() => {
  const colorsString = props.colors.map((color) => `${color.color} ${color.stop}`).join(", ");

  if (props.gradientType === "linear" || props.gradientType === "conic") {
    return `linear-gradient(45deg, ${colorsString})`;
  } else if (props.gradientType === "radial") {
    return `radial-gradient(circle, ${colorsString})`;
  }

  return `linear-gradient(45deg, ${colorsString})`;
});

// Convert stop percentage to number
const getStopPercentage = (stop: string): number => {
  return parseInt(stop.replace("%", "")) || 0;
};

// Handle bar click to add new color stop
const handleBarClick = (event: MouseEvent) => {
  if (!gradientBar.value) {
    return;
  }

  const rect = gradientBar.value.getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const percentage = Math.round((clickX / rect.width) * 100);

  // Find the closest existing stop
  const closestStop = props.colors.reduce((closest, color) => {
    const currentStop = getStopPercentage(color.stop);
    const closestStop = getStopPercentage(closest.stop);
    return Math.abs(currentStop - percentage) < Math.abs(closestStop - percentage)
      ? color
      : closest;
  });

  // Only add if we're not too close to existing stops
  const closestStopValue = getStopPercentage(closestStop.stop);
  if (Math.abs(percentage - closestStopValue) > 5) {
    emit("add-color", percentage);
  }
};

// Start dragging a color handle
const startDrag = (id: number, event: MouseEvent | TouchEvent) => {
  event.preventDefault();
  isDragging.value = true;
  draggedId.value = id;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  dragStartX.value = clientX;

  if (gradientBar.value) {
    const rect = gradientBar.value.getBoundingClientRect();
    barStartX.value = rect.left;
  }

  document.addEventListener("mousemove", handleDrag);
  document.addEventListener("mouseup", stopDrag);
  document.addEventListener("touchmove", handleDrag);
  document.addEventListener("touchend", stopDrag);
};

// Handle drag movement
const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value || !draggedId.value || !gradientBar.value) {
    return;
  }

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const rect = gradientBar.value.getBoundingClientRect();

  let percentage = ((clientX - rect.left) / rect.width) * 100;
  percentage = Math.max(0, Math.min(100, percentage));

  // Find the color and update its stop
  const colorIndex = props.colors.findIndex((c) => c.id === draggedId.value);
  if (colorIndex !== -1) {
    const updatedColors = [...props.colors];
    updatedColors[colorIndex] = {
      ...updatedColors[colorIndex],
      stop: `${Math.round(percentage)}%`,
    };
    emit("update:colors", updatedColors);
  }
};

const stopDrag = () => {
  isDragging.value = false;
  draggedId.value = null;

  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
};

const updateColor = (id: number, field: "color" | "stop", value: string) => {
  emit("update-color", id, field, value);
};

const addColor = () => {
  emit("add-color");
};

const removeColor = (id: number) => {
  emit("remove-color", id);
};

const isDefaultColor = (color: ColorStop) => {
  return color.id === 1 || color.id === 10;
};

const getColorLabel = (color: ColorStop) => {
  if (color.id === 1) {
    return t("common.startColor");
  } else if (color.id === 10) {
    return t("common.endColor");
  }

  // Only consider colors with ID 2-9 as middle colors (added colors)
  const middleColors = props.colors.filter((c) => c.id >= 2 && c.id <= 9);

  // Sort middle colors by ID to get their creation order
  const sortedMiddleColors = middleColors.sort((a, b) => a.id - b.id);

  const colorIndex = sortedMiddleColors.findIndex((c) => c.id === color.id);
  return `${t("common.color")} ${colorIndex + 1}`;
};

const getStopDisplayStyle = (stop: string) => {
  const percentage = getStopPercentage(stop);

  // Define safe zones (percentage from edges where we start adjusting)
  const safeZone = 15; // 15% from each edge

  // Calculate if we're in the left or right edge zone
  const isNearLeftEdge = percentage < safeZone;
  const isNearRightEdge = percentage > 100 - safeZone;

  let leftPosition = 50; // Default center position
  let transform = "translateX(-50%)"; // Default center transform

  if (isNearLeftEdge) {
    // Near left edge: shift right to prevent clipping
    // The closer to the edge, the more we shift right
    const shiftAmount = Math.max(0, ((safeZone - percentage) / safeZone) * 20);
    leftPosition = 50 + shiftAmount;
    transform = "translateX(-20%)"; // Shift the transform point to the left side
  } else if (isNearRightEdge) {
    // Near right edge: shift left to prevent clipping
    // The closer to the edge, the more we shift left
    const shiftAmount = Math.max(0, ((percentage - (100 - safeZone)) / safeZone) * 20);
    leftPosition = 50 - shiftAmount;
    transform = "translateX(-80%)"; // Shift the transform point to the right side
  }

  return {
    left: `${leftPosition}%`,
    transform: transform,
  };
};
</script>

<style scoped>
.gradient-color-slider {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.gradient-bar-container {
  position: relative;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gradient-bar {
  position: relative;
  width: 97%;
  height: 20px;
  border-radius: 4px;
  background: linear-gradient(45deg, #ffffff, #188bf6);
  cursor: copy;
  overflow: visible;
}

.color-handle {
  position: absolute;
  top: -6px;
  width: 16px;
  height: 32px;
  border: 1.5px solid #155eef;
  background: #ffffff;
  border-radius: 8px;
  cursor: grab;
  z-index: 10;
}

.color-handle:active {
  cursor: grabbing;
  z-index: 100;
}

.stop-display {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 100%;
  padding-top: 8px;
  padding-right: 12px;
  padding-bottom: 8px;
  padding-left: 12px;
  margin-top: 4px;
  background: white;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  color: #374151;
  white-space: nowrap;
  z-index: 5;
  height: 34px;
  width: 46px;
  box-shadow: 0px 1px 2px 0px #1018280f, 0px 1px 3px 0px #1018281a;
  transition: left 0.2s ease, transform 0.2s ease;
}

.color-stops-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.color-stop-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
}

.color-label {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  line-height: 16px;
  min-width: 80px;
  text-align: left;
  flex-shrink: 0;
}

.right-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.color-picker-container {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
}

.color-picker-container .n-color-picker {
  width: 32px !important;
  height: 32px !important;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
}

.stop-input-container {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Touch device optimizations */
@media (hover: none) {
  .color-handle {
    min-width: 32px;
    min-height: 64px;
  }
}
</style>
